import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { injectIntl, intlShape, defineMessages } from 'react-intl';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableFooter from '@material-ui/core/TableFooter';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import muiWithStyles from '@material-ui/core/styles/withStyles';
import classNames from 'classnames';
import { convertFromRaw, EditorState } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import _ from 'lodash';
import moment from 'moment';
import { withCookies, Cookies } from 'react-cookie';

import { Interweave } from 'interweave';
import * as discussionsActions from '../../actions/discussions';
import * as editDiscussionActions from '../../actions/editDiscussion';
import { buildQuery, isAdmin, isOwner } from '../../helpers';

import CircularProgressLoader from '../CircularProgressLoader/CircularProgressLoader';
import EnhancedTableHead from '../EnhancedTableHead/EnhancedTableHead';
import Link from '../Link/Link';
import UserAvatar from '../UserAvatar/UserAvatar';
import CustomModal from '../Modals/Modal';
import ContentContainer from '../statelessComponents/ScreenParts/ContentContainer';
import SkholeIcon53 from '../statelessComponents/Icons/SkholeIcon53';
import SkholeIcon52 from '../statelessComponents/Icons/SkholeIcon52';

import {
  ResponsiveTableBody,
  TablePagination,
} from '../statelessComponents/Table';

import EnhancedTableToolbar from './EnhancedTableToolbar/EnhancedTableToolbar';
import EditDiscussionModal from '../EditDiscussionModal/EditDiscussionModal';

import s from './Discussions.scss';

const messages = defineMessages({
  discussionColumn: {
    id: 'Discussions.discussionColumn',
    defaultMessage: 'Discussion',
    description: 'Second column label on discussion page',
  },
  latestColumn: {
    id: 'Discussions.latestColumn',
    defaultMessage: 'Latest',
    description: 'Third column label on discussion page',
  },
  responsesColumn: {
    id: 'Discussions.responsesColumn',
    defaultMessage: 'Responses',
    description: 'Fourth column label on discussion page',
  },
});

const styles = () => ({
  tableHeaderCell: {
    padding: '4px 56px 4px 24px',
  },
  tableCell: {
    padding: '4px 56px 4px 24px',
    fontSize: '0.8125rem',
    '&:last-child': {
      'padding-right': '24px',
    },
  },
});

@injectIntl
@muiWithStyles(styles)
@withStyles(s)
@withCookies
@connect(
  state => ({
    auth: state.auth,
    loading: state.discussions.loading,
    loaded: state.discussions.loaded,
    discussions: state.discussions.discussions,
    error: state.discussions.error,
    total: state.discussions.total,
    lang: state.intl.locale,
  }),
  dispatch =>
    bindActionCreators(
      {
        ...discussionsActions,
        ...editDiscussionActions,
      },
      dispatch,
    ),
)
export default class Discussions extends React.Component {
  static propTypes = {
    error: PropTypes.object, // eslint-disable-line
    auth: PropTypes.object, // eslint-disable-line
    loading: PropTypes.bool.isRequired,
    loaded: PropTypes.bool.isRequired,
    total: PropTypes.number.isRequired,
    discussions: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
      }),
    ).isRequired,
    courseId: PropTypes.string,
    showFollowed: PropTypes.bool,
    getDiscussions: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    resetDiscussion: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    cookies: PropTypes.instanceOf(Cookies).isRequired,
    disableAutoload: PropTypes.bool,
    classes: PropTypes.shape({
      tableHeaderCell: PropTypes.object, // eslint-disable-line
      tableCell: PropTypes.object, // eslint-disable-line
    }).isRequired,
  };

  static contextTypes = {
    path: PropTypes.string,
    fetch: PropTypes.func.isRequired,
  };

  static defaultProps = {
    courseId: '',
    showFollowed: false,
    disableAutoload: false,
  };

  constructor(props) {
    super(props);
    this.loadDiscussionsWithDebounce = _.debounce(
      this.clearAndLoadDiscussions,
      300,
    );
  }

  state = {
    search: '',
    perPage: 50,
    page: 0,
    order: 'desc',
    orderBy: 'createDate',
    sort: '-createDate',
    isPostModalOpened: false,
    discussionEditor: [],
    mainGroup: null,
    mainGroupSearchValue: '',
  };

  componentDidMount() {
    if (this.props.disableAutoload) {
      return;
    }

    this.loadDiscussions();
  }

  componentWillReceiveProps(nextProps) {
    if (_.get(this, 'props.discussions') !== _.get(nextProps, 'discussions')) {
      this.setState({
        discussionEditor: _.map(nextProps.discussions, ({ text }) =>
          EditorState.createWithContent(convertFromRaw(text)),
        ),
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.loading && !this.props.loading && window !== undefined) {
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }
  }

  onSearchChange = event => {
    this.setState(
      {
        search: event.target.value,
        page: 0,
      },
      this.loadDiscussionsWithDebounce,
    );
  };

  onMainGroupChange = mainGroup => {
    this.setState(
      {
        mainGroup: Array.isArray(mainGroup) ? null : mainGroup, // somewhy backspace make state []
      },
      this.clearAndLoadDiscussions,
    );
  };

  onMainGroupNameChange = mainGroupSearchValue => {
    this.setState({ mainGroupSearchValue });
  };

  getMainGroups = value => {
    const { fetch } = this.context;

    const params = {
      sort: 'name',
      page: 1,
      search: _.trim(value),
      perPage: 50,
    };
    const query = buildQuery(params);

    return fetch(`/api/main_groups?${query}`, { method: 'GET' })
      .then(async res => {
        const resText = await res.text();
        const total = res.headers.get('x-total-count') || 0;
        this.setState({ mainGroupTotal: total });
        const json = resText ? JSON.parse(resText) : [];
        if (res.status >= 400) {
          console.error(json);
          return [];
        }

        return json.map(item => ({
          _id: item._id,
          name: item.name,
        }));
      }, console.error)
      .catch(console.error);
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  setVideoFormat = () => {
    this.props.cookies.set(
      'contentTypeSwitchers',
      {
        video: true,
        text: true,
        audio: false,
      },
      { path: '/', domain: window.App.cookieDomain },
    );
  };

  clearSearch = () => {
    this.setState(
      {
        search: '',
        page: 0,
      },
      this.clearAndLoadDiscussions,
    );
  };

  handleChangePage = (event, page) => {
    this.setState({ page }, this.loadDiscussions);
  };

  loadDiscussions = () => {
    const { getDiscussions, courseId, showFollowed, auth, reset } = this.props;
    const { search, perPage, page, sort, mainGroup } = this.state;

    const params = {
      sort,
      page: page + 1,
      search: search.trim(),
      perPage,
    };

    if (courseId) {
      params.courseId = courseId;
    }

    if (showFollowed) {
      params.followed = showFollowed;
    }

    if (mainGroup) {
      params.mainGroupId = mainGroup._id;
    }

    if (!mainGroup && !courseId && isAdmin(auth)) {
      return reset();
    }

    return getDiscussions(params);
  };

  handleRequestSort = (event, property) => {
    const orderBy = property;
    let order = 'desc';

    if (this.state.orderBy === property && this.state.order === 'desc') {
      order = 'asc';
    }

    const sort = `${order === 'desc' ? '-' : ''}${orderBy}`;

    this.setState({ order, orderBy, sort, page: 0 }, this.loadDiscussions);
  };

  clearAndLoadDiscussions = () => {
    this.loadDiscussions();
  };

  openPostModal = () => {
    this.props.resetDiscussion();
    this.setState({
      isPostModalOpened: true,
    });
  };

  closeEditDiscussionModal = () => {
    this.setState({
      isPostModalOpened: false,
    });
    this.props.resetDiscussion();
  };

  saveEditDiscussionModal = () => {
    this.clearSearch();
  };

  render() {
    const {
      error,
      loading,
      loaded,
      discussions,
      total,
      courseId,
      intl: { formatMessage },
      auth,
      classes,
    } = this.props;

    const {
      search,
      order,
      orderBy,
      perPage,
      page,
      isPostModalOpened,
      discussionEditor,
      mainGroup,
      mainGroupSearchValue,
      mainGroupTotal,
    } = this.state;

    const { path } = this.context;

    if (error) {
      return (
        <div className={s.root}>
          <div className={s.container}>
            <Typography gutterBottom color="error">
              {error.error}
            </Typography>
          </div>
        </div>
      );
    }

    return (
      <ContentContainer fullWidthOnMobile>
        <div className={s.toolbarWrapper}>
          <EnhancedTableToolbar
            search={search}
            onSearchChange={this.onSearchChange}
            clearSearch={this.clearSearch}
            openPostModal={this.openPostModal}
            onMainGroupChange={this.onMainGroupChange}
            changeMainGroupSearchField={this.onMainGroupNameChange}
            mainGroup={mainGroup}
            loadOptions={this.getMainGroups}
            loadOptionsTotal={mainGroupTotal}
            courseId={courseId}
            mainGroupSearchValue={mainGroupSearchValue}
            loading={loading}
          />
        </div>
        <div className={s.tableWrapper}>
          {loading && (
            <CircularProgressLoader
              rootStyle={{ position: 'absolute', zIndex: 10 }}
            />
          )}
          <Table
            className={classNames(s.table, {
              [s.loading]: loading,
            })}
          >
            <EnhancedTableHead
              classNameHead={s.tableHead}
              classesNameRow={{ root: s.tableRow }}
              classesSortLabel={{
                icon: s.sortIcon,
              }}
              order={order}
              orderBy={orderBy}
              onRequestSort={this.handleRequestSort}
              rowCount={total}
              columnData={[
                {
                  id: 'avatar',
                  disablePadding: true,
                  label: '',
                  cellClassName: s.notDisplayOnMobile,
                },
                {
                  id: 'createDate',
                  numeric: false,
                  disablePadding: false,
                  label: formatMessage(messages.discussionColumn),
                  cellClassName: classes.tableHeaderCell,
                },
                {
                  id: 'latestResponseDate',
                  numeric: false,
                  disablePadding: false,
                  label: formatMessage(messages.latestColumn),
                  cellClassName: classNames(
                    s.notDisplayOnMobile,
                    classes.tableHeaderCell,
                  ),
                },
                {
                  id: 'responses',
                  numeric: false,
                  disablePadding: false,
                  label: formatMessage(messages.responsesColumn),
                  cellClassName: classNames(
                    s.notDisplayOnMobile,
                    classes.tableHeaderCell,
                  ),
                },
              ]}
            />
            <ResponsiveTableBody isEmpty={loaded && _.isEmpty(discussions)}>
              {discussions.map((n, index) => {
                let latestResponse = null;

                if (n.latestResponse) {
                  latestResponse = (
                    <div>
                      <div>
                        {moment(n.latestResponse.createDate).format(
                          'DD/MM/YYYY',
                        )}
                      </div>
                      <div>
                        {moment(n.latestResponse.createDate).format('HH:mm')}
                      </div>
                      <Typography
                        className={s.discussionAuthor}
                        component={Link}
                        to={`/profiles/${_.get(n, 'createdBy._id')}`}
                      >
                        {`${_.get(
                          n.latestResponse,
                          'createdBy.lastName',
                          '',
                        )} ${_.get(
                          n.latestResponse,
                          'createdBy.firstName',
                          '',
                        )}`}
                      </Typography>
                    </div>
                  );
                }

                return (
                  <TableRow hover tabIndex={-1} key={n._id} className={s.row}>
                    <TableCell
                      className={classNames(
                        s.tableCell,
                        s.avatarCell,
                        s.notDisplayOnMobile,
                      )}
                      style={{ width: '50px' }}
                      size="small"
                    >
                      <Tooltip
                        title={`${_.get(n, 'createdBy.lastName', '')} ${_.get(
                          n,
                          'createdBy.firstName',
                          '',
                        )}`}
                      >
                        {n.createdBy && (
                          <UserAvatar user={n.createdBy} className={s.avatar} />
                        )}
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      className={classNames(
                        s.tableCell,
                        s.discussionCell,
                        classes.tableCell,
                      )}
                    >
                      <div className={classNames(s.flex, s.alignItemsCenter)}>
                        <Typography
                          className={s.discussionSubject}
                          component={Link}
                          to={`${path.replace(/\/$/, '')}/${n._id}`}
                          noModification
                        >
                          {n.name}
                        </Typography>
                      </div>

                      <Typography
                        className={s.discussionMessage}
                        component={Link}
                        to={`${path.replace(/\/$/, '')}/${n._id}`} // todo check
                        noModification
                      >
                        <Interweave
                          tagName="div"
                          content={
                            discussionEditor[index]
                              ? `${stateToHTML(
                                  discussionEditor[index].getCurrentContent(),
                                )}`
                              : ''
                          }
                        />
                      </Typography>
                      <div>
                        {n.courseId && (
                          <Tooltip title={n.courseId.name}>
                            <IconButton
                              component={Link}
                              to={`/courses/${n.courseId.slug}`}
                            >
                              {/* <CourseIcon /> */}
                              <SkholeIcon52 className={s.courseIcon} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {n.courseId && n.lessonId && (
                          <Tooltip
                            title={
                              n.lessonId.name +
                              (isOwner(auth, n.videoNoteCreatedBy, false)
                                ? ` (${moment(n.videoNote.time * 1000).format(
                                    'mm:ss',
                                  )}`
                                : '')
                            }
                          >
                            <IconButton
                              component={Link}
                              to={`/courses/${n.courseId.slug}/lessons/${
                                n.lessonId.slug
                              }${
                                isOwner(auth, n.videoNoteCreatedBy, false)
                                  ? `?videoNoteId=${n.videoNoteId}`
                                  : ''
                              }`}
                            >
                              {/* <LessonIcon /> */}
                              <SkholeIcon53 className={s.lessonIcon} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                      <div className={s.flex}>
                        <Typography className={s.discussionDate}>
                          {moment(n.createDate).format('DD/MM/YYYY HH:mm')}
                        </Typography>
                        &nbsp;
                        <Typography
                          className={s.discussionAuthor}
                          component={Link}
                          to={`/profiles/${_.get(n, 'createdBy._id')}`}
                        >
                          {_.get(n, 'createdBy.lastName', '')}
                          &nbsp;
                          {_.get(n, 'createdBy.firstName', '')}
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell
                      className={classNames(
                        s.tableCell,
                        s.tableCellGrey,
                        s.notDisplayOnMobile,
                        classes.tableCell,
                      )}
                    >
                      {latestResponse}
                    </TableCell>
                    <TableCell
                      className={classNames(
                        s.tableCell,
                        s.tableCellGrey,
                        s.notDisplayOnMobile,
                        classes.tableCell,
                      )}
                    >
                      {n.responses}
                    </TableCell>
                  </TableRow>
                );
              })}
            </ResponsiveTableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  colSpan={4}
                  count={total}
                  rowsPerPage={perPage}
                  page={page}
                  backIconButtonProps={{
                    classes: {
                      root: s.pagination,
                    },
                  }}
                  nextIconButtonProps={{
                    classes: {
                      root: s.pagination,
                    },
                  }}
                  rowsPerPageOptions={[perPage]}
                  onChangePage={this.handleChangePage}
                  classes={{
                    caption: s.pagination,
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </div>
        <CustomModal
          isOpen={isPostModalOpened}
          onClose={this.closeEditDiscussionModal}
        >
          <EditDiscussionModal
            onClose={this.closeEditDiscussionModal}
            onSave={this.saveEditDiscussionModal}
            courseId={courseId}
            mainGroup={mainGroup}
            autoFollow
          />
        </CustomModal>
      </ContentContainer>
    );
  }
}
